/**
 * WuaPlusSample — public preview of a WUA Personalised Report at
 * `/wua-plus/sample`. Un-authenticated so cold visitors can see the
 * report format before paying $49. Content is a hand-crafted sample
 * (not generated live) — represents the shape and voice of a real
 * report without giving away Claude compute.
 *
 * Positioning (2026-02-05): the $49 report is a standalone WUA
 * service that sits BEFORE WUA+. WUA+ is the 1:1 professional-advisor
 * upgrade families take AFTER reading their report. UI copy keeps
 * "WUA" clean of the "+" on the report itself.
 */
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Sparkles, ArrowRight, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import ReactMarkdown from "react-markdown";
import PathwaySEO from "@/components/app/PathwaySEO";

const SAMPLES = {
  uk_business: {
    label: "UK · Business",
    markdown: `# WUA Personalised Report

*Sample — this is what a real WUA Personalised Report for a family looks like. Every real report is generated fresh from your own profile, shortlist and Advanced Search results.*

## Executive Verdict

You've built a serious Computer Science shortlist across the UK and US on a £120,000 three-year envelope, with Goldman Sachs as your named career target. Your AAB predicted grades put you inside the offer range at seven of your ten choices, but three of them will need a strategic backup. The bigger risk isn't academic — it's that your budget assumes UK home-fee status, which one of your US choices (Georgia Tech) can't offer at any tuition level. This report ranks your ten choices by *combined* academic + financial + visa risk, not by prestige, and tells you what to fix first.

## Portfolio Balance

Your current split is 4 aspirational (Imperial, UCL, Cambridge, MIT), 3 competitive (Warwick, Southampton, Georgia Tech), 2 realistic (Nottingham, Bath), and 1 secure (Sussex). That's 40% aspirational — a common trap. Even at AAB you should carry at least 3 secure choices; a single unexpected exam grade drops one aspirational and one competitive into "reach", leaving only Sussex as a genuine fallback. Add Portsmouth or Hertfordshire as a second secure choice before the January 25 UCAS deadline.

## Financial Reality Check

At home-fee status your total three-year cost sits at £29,250 (tuition) + £45,000 (living, London-adjusted) = **£74,250**, leaving a healthy £45,750 buffer inside your £120,000 envelope. Georgia Tech's international fees run $38,000/yr × 4 years = $152,000, which alone exceeds your total budget — remove it unless you secure a Buzz Scholarship (25 available for CS, ~9% acceptance). MIT's need-based aid programme means the £120k envelope *is* feasible if you complete the CSS Profile by 15 Feb 2027.

## Visa & Mobility

You hold a UK passport and are legally resident in the UK for 4 years continuously, which secures home-fee status on every UK choice — verify with each admissions office to confirm the ordinary residency test. US visa preparation for MIT should start now: SAT booking, TB test, and DS-160 all take 6-10 weeks. Your Goldman Sachs target implies you'll want the F-1 STEM-OPT 3-year extension after graduation — Georgia Tech and MIT both qualify; if you drop them, you'll be graduating into a UK Skilled Worker route instead.

## Application Timeline

- **Now → 25 Jan 2027**: Add 2 secure UK choices; submit UCAS with all five UK entries.
- **1 Feb → 15 Feb 2027**: Complete MIT CSS Profile + submit SAT scores.
- **1 Mar → 15 Mar 2027**: Book IELTS (any US-only application needs it) and TB test.
- **15 Apr → 1 May 2027**: Attend Spring Weeks at Goldman Sachs / other IB firms — apply the moment banking spring-week portals open in Sept 2027 for the following summer.

---

*This sample is illustrative; every real WUA Personalised Report is built from your own profile snapshot and delivered within 60 seconds of payment. Reply to* [corrections@whichuniversityadvisor.com](mailto:corrections@whichuniversityadvisor.com) *with any factual corrections you find in your real report — we honour a 48-hour money-back guarantee. Want to talk it through 1:1? A WUA+ professional advisor session is a separate premium service you can add after the report lands.*`,
  },
  us_cs: {
    label: "US · Computer Science",
    markdown: `# WUA Personalised Report

*Sample — this is what a real WUA Personalised Report for a US-bound family looks like. Every real report is generated fresh from your own profile, shortlist and Advanced Search results.*

## Executive Verdict

You've built a strong Computer Science shortlist across US public flagships + two Ivies, with a $200,000 four-year envelope and Google as your named career target. Your 1450 SAT + 4.1 unweighted GPA puts you inside the middle-50% at eight of your twelve choices, but the two Ivies (Stanford, Cornell) sit in the top-quartile academic band where holistic factors will dominate. The bigger risk isn't academic — it's that six of your out-of-state public flagships publish full sticker prices ~$55k/yr with almost no merit aid for non-residents. This report ranks by combined academic + affordability + Google-pipeline strength, and flags the two schools where your budget silently breaks.

## Portfolio Balance

Split: 2 aspirational (Stanford, Cornell), 4 competitive (Georgia Tech, UT Austin, UIUC, UMich), 4 realistic (UMD, Purdue, UMass, Rutgers), 2 secure (SUNY Stony Brook, ASU). That's healthy for the target — you have real safeties and two truly ambitious picks. Consider dropping one competitive to make room for a private CS-strong school with generous merit aid (Case Western, Northeastern) — they typically convert middle-50% profiles to $25k+/yr scholarships.

## Financial Reality Check

At sticker: UT Austin OOS $57k/yr × 4 = $228k, already over budget alone. UMich OOS $73k/yr is $292k — well over. Both need to move to the "aspire but only if aid comes through" tier. Purdue OOS at $30k/yr × 4 = $120k fits comfortably. SUNY Stony Brook OOS ~$48k/yr is close to the ceiling. Best value in your list on 5-year LEO-equivalent (Scorecard) data: Georgia Tech (median grad salary $87k, 1-year post) and UIUC ($92k) — both give you a Google pipeline for under $200k total cost.

## Visa & Mobility

You hold a US passport, so no visa gate for US options — but if you later swap in a UK or EU school, the £/€-to-USD conversion favors the UK on tuition (Imperial CS: £39k/yr international ≈ $50k) while EU public schools (TU Delft, TU Munich) are essentially free. Worth keeping one EU option open as an "affordability optionality" pick, especially given Google DevSubs is expanding fast in Zurich + Munich.

## Application Timeline

- **Now → 1 Nov 2026**: Common App + supplements for Stanford + Cornell early action (both due 1 Nov).
- **1 Dec 2026**: UT Austin priority deadline (they close by Feb 15 but fill on rolling basis).
- **1 Jan 2027**: Regular decision Common App submission for the remaining nine.
- **15 Feb 2027**: CSS Profile submitted for every school that needs it — do this once, apply to all.
- **May 2027 → Aug 2027**: Apply to Google STEP internship (freshman/sophomore program, applications open Sept 2027 for summer 2028).

---

*This sample is illustrative; every real WUA Personalised Report is built from your own profile snapshot and delivered within 60 seconds of payment. Reply to* [corrections@whichuniversityadvisor.com](mailto:corrections@whichuniversityadvisor.com) *with any factual corrections you find in your real report — we honour a 48-hour money-back guarantee. Want to talk it through 1:1? A WUA+ professional advisor session is a separate premium service you can add after the report lands.*`,
  },
};

export default function WuaPlusSample() {
  const [params, setParams] = useSearchParams();
  const initial = SAMPLES[params.get("variant")] ? params.get("variant") : "uk_business";
  const [variant, setVariant] = useState(initial);
  const s = SAMPLES[variant];
  return (
    <div data-testid="wua-plus-sample-page" className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
      <PathwaySEO
        title="See a sample WUA Personalised Report — $49"
        description="Preview of the WUA Personalised Report format: five sourced sections covering portfolio balance, financial reality, visa preparation, application timeline and an editor's verdict. Pre-WUA+ · standalone $49 service."
        path="/wua-plus/sample"
      />
      <Link
        to="/plus"
        className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-[#4C1D95]"
        data-testid="wua-plus-sample-back"
      >
        <ArrowLeft className="h-3 w-3" /> Back to the report
      </Link>

      <div className="mt-6 flex items-center gap-1.5 text-[11px] font-underground-sc tracking-wider text-amber-800 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 w-fit">
        <Info className="h-3 w-3" /> Sample · Hand-written illustration of the format
      </div>

      {/* Variant switcher — cold visitors can flip between the UK
          and US Computer Science samples to see the same format
          applied to their own country context. URL syncs so links
          shared into press or Reddit deep-link to the right variant. */}
      <div
        data-testid="wua-plus-sample-variant-switcher"
        className="mt-4 inline-flex items-center gap-1 rounded-md border border-stone-200 bg-stone-50 p-1"
      >
        {Object.entries(SAMPLES).map(([key, val]) => (
          <button
            key={key}
            type="button"
            data-testid={`wua-plus-sample-variant-${key}`}
            onClick={() => { setVariant(key); setParams({ variant: key }, { replace: true }); }}
            className={`px-3 py-1.5 rounded text-[12px] font-underground-sc tracking-wider transition-colors ${
              variant === key
                ? "bg-white text-[#4C1D95] shadow-sm"
                : "text-stone-600 hover:text-stone-900"
            }`}
          >
            {val.label}
          </button>
        ))}
      </div>

      <article
        data-testid="wua-plus-sample-body"
        className="mt-6 prose prose-stone max-w-none prose-headings:font-editorial prose-headings:text-[#002147] prose-h1:text-4xl prose-h2:text-2xl prose-a:text-[#4C1D95]"
      >
        <ReactMarkdown>{s.markdown}</ReactMarkdown>
      </article>

      <div
        data-testid="wua-plus-sample-cta"
        className="mt-10 rounded-xl border-2 border-[#4C1D95] bg-white p-6"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-[11px] font-underground-sc tracking-wider text-emerald-800">
              <Sparkles className="h-3 w-3" />
              Ready to buy yours?
            </div>
            <h2 className="mt-3 font-editorial text-2xl text-[#002147]">
              Your real report will be generated from your own profile in 60 seconds.
            </h2>
            <p className="mt-2 text-[13px] text-stone-600 leading-relaxed max-w-2xl">
              $49 one-off · No subscription · 48-hour refund promise · Downloadable + emailed. Pre-WUA+ · a standalone WUA service.
            </p>
          </div>
          <Button
            asChild
            size="lg"
            className="bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-md"
            data-testid="wua-plus-sample-buy-btn"
          >
            <Link to="/plus">
              Buy my report — $49 <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </Button>
        </div>
        <div className="mt-4 flex items-center gap-1.5 text-[11px] font-underground-sc tracking-wider text-stone-500">
          <ShieldCheck className="h-3 w-3 text-emerald-700" />
          Secure checkout · Stripe · Your data is never shared
        </div>
      </div>
    </div>
  );
}
